import { useState, useRef, useEffect, useLayoutEffect, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/utils'

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'
export type TooltipAlign = 'start' | 'center' | 'end'

export interface TooltipProps {
  children: ReactNode
  content: string
  show: boolean
  position?: TooltipPosition
  align?: TooltipAlign
  offset?: number
  className?: string
  contentClassName?: string
  arrowClassName?: string
  delay?: number
  disabled?: boolean
}

export function Tooltip({
  children,
  content,
  show,
  position = 'right',
  align = 'center',
  offset = 8,
  className,
  contentClassName,
  arrowClassName,
  delay = 0,
  disabled = false
}: TooltipProps) {
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [isVisible, setIsVisible] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Toggle visibility (with optional delay) whenever `show` changes. The actual
  // positioning happens in the layout effect below, once the tooltip is in the DOM.
  useEffect(() => {
    if (show && !disabled) {
      if (delay > 0) {
        timeoutRef.current = setTimeout(() => {
          setIsVisible(true)
        }, delay)
      } else {
        setIsVisible(true)
      }
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      setIsVisible(false)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [show, disabled, delay])

  // Position the tooltip against the trigger using its REAL measured size, so
  // the arrow lands on the trigger even when the content wraps to several lines.
  // Runs before paint (layout effect) to avoid a flash at the wrong spot.
  useLayoutEffect(() => {
    if (!isVisible || disabled || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const tt = tooltipRef.current
    const tooltipRect = tt
      ? { width: tt.offsetWidth, height: tt.offsetHeight }
      : { width: 200, height: 40 }

    let x = 0, y = 0

    switch (position) {
      case 'right':
        x = rect.right + offset
        y = align === 'start' ? rect.top :
            align === 'end' ? rect.bottom - tooltipRect.height :
            rect.top + rect.height / 2
        break
      case 'left':
        x = rect.left - tooltipRect.width - offset
        y = align === 'start' ? rect.top :
            align === 'end' ? rect.bottom - tooltipRect.height :
            rect.top + rect.height / 2
        break
      case 'top':
        x = align === 'start' ? rect.left :
            align === 'end' ? rect.right - tooltipRect.width :
            rect.left + rect.width / 2
        y = rect.top - tooltipRect.height - offset
        break
      case 'bottom':
        x = align === 'start' ? rect.left :
            align === 'end' ? rect.right - tooltipRect.width :
            rect.left + rect.width / 2
        y = rect.bottom + offset
        break
    }

    setTooltipPosition({ x, y })
  }, [isVisible, disabled, position, align, offset, content])

  const getTransform = () => {
    switch (position) {
      case 'right':
      case 'left':
        return align === 'start' ? 'translateY(0)' :
               align === 'end' ? 'translateY(-100%)' :
               'translateY(-50%)'
      case 'top':
      case 'bottom':
        return align === 'start' ? 'translateX(0)' :
               align === 'end' ? 'translateX(-100%)' :
               'translateX(-50%)'
      default:
        return 'translateY(-50%)'
    }
  }

  const getArrowStyles = () => {
    const arrowSize = 6
    const arrowOffset = 1
    
    switch (position) {
      case 'right':
        return {
          outline: {
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            borderTop: `${arrowSize}px solid transparent`,
            borderBottom: `${arrowSize}px solid transparent`,
            borderRight: `${arrowSize}px solid hsl(var(--primary))` // matches the bubble's border-primary
          },
          fill: {
            right: `calc(100% - ${arrowOffset}px)`,
            top: '50%',
            transform: 'translateY(-50%)',
          },
          fillClasses: `border-r-[5px] border-r-white dark:border-r-gray-800 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent`
        }
      case 'left':
        return {
          outline: {
            left: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            borderTop: `${arrowSize}px solid transparent`,
            borderBottom: `${arrowSize}px solid transparent`,
            borderLeft: `${arrowSize}px solid hsl(var(--primary))`
          },
          fill: {
            left: `calc(100% - ${arrowOffset}px)`,
            top: '50%',
            transform: 'translateY(-50%)',
          },
          fillClasses: `border-l-[5px] border-l-white dark:border-l-gray-800 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent`
        }
      case 'top':
        return {
          outline: {
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: `${arrowSize}px solid transparent`,
            borderRight: `${arrowSize}px solid transparent`,
            borderTop: `${arrowSize}px solid hsl(var(--primary))`
          },
          fill: {
            top: `calc(100% - ${arrowOffset}px)`,
            left: '50%',
            transform: 'translateX(-50%)',
          },
          fillClasses: `border-t-[5px] border-t-white dark:border-t-gray-800 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent`
        }
      case 'bottom':
        return {
          outline: {
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: `${arrowSize}px solid transparent`,
            borderRight: `${arrowSize}px solid transparent`,
            borderBottom: `${arrowSize}px solid hsl(var(--primary))`
          },
          fill: {
            bottom: `calc(100% - ${arrowOffset}px)`,
            left: '50%',
            transform: 'translateX(-50%)',
          },
          fillClasses: `border-b-[5px] border-b-white dark:border-b-gray-800 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent`
        }
      default:
        return {
          outline: {},
          fill: {},
          fillClasses: ''
        }
    }
  }

  const arrowStyles = getArrowStyles()

  const getAnimationName = () => {
    switch (position) {
      case 'right':
        return 'slideInFromRight'
      case 'left':
        return 'slideInFromLeft'
      case 'top':
        return 'slideInFromTop'
      case 'bottom':
        return 'slideInFromBottom'
      default:
        return 'slideInFromRight'
    }
  }

  return (
    <>
      <div ref={triggerRef} className={className}>
        {children}
      </div>
      {show && !disabled && isVisible && createPortal(
        <div
          ref={tooltipRef}
          className={cn(
            "fixed bg-white dark:bg-gray-800 text-foreground text-sm px-3 py-2 rounded-md shadow-lg whitespace-nowrap z-[9999] pointer-events-none border border-primary",
            contentClassName
          )}
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: getTransform(),
            animation: `${getAnimationName()} 200ms ease-out forwards`
          }}
        >
          {content}
          {/* Arrow - Blue outline */}
          <div
            className={cn("absolute w-0 h-0", arrowClassName)}
            style={arrowStyles.outline}
          />
          {/* Arrow - Fill */}
          <div
            className={cn("absolute w-0 h-0", arrowStyles.fillClasses, arrowClassName)}
            style={arrowStyles.fill}
          />
        </div>,
        document.body
      )}
      {show && !disabled && isVisible && createPortal(
        <style>{`
          @keyframes slideInFromRight {
            from {
              opacity: 0;
              transform: translateY(-50%) translateX(8px);
            }
            to {
              opacity: 1;
              transform: translateY(-50%) translateX(0);
            }
          }
          @keyframes slideInFromLeft {
            from {
              opacity: 0;
              transform: translateY(-50%) translateX(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(-50%) translateX(0);
            }
          }
          @keyframes slideInFromTop {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
          @keyframes slideInFromBottom {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}</style>,
        document.head
      )}
    </>
  )
} 